import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

export interface AssignedLesson {
  assignmentId: string
  lessonId: string
  lessonTitle: string
  lessonDescription: string | null
  assignedAt: string
  inCarCompletedAt: string | null
  videoUnlockedAt: string | null
  // Populated only when the student's own RLS allows reading them (migration 066's
  // "student read assigned" policy on curriculum_lesson_assets) — pdf is visible as soon
  // as the lesson is assigned; video only appears once videoUnlockedAt is set.
  pdfAssetId: string | null
  videoAssetId: string | null
}

/** A student's own assigned lessons, with whichever assets their RLS currently allows
 * them to see (migration 066) — the query itself is the access gate; no client-side
 * filtering is layered on top of it. */
export function useStudentCurriculum() {
  const { user } = useAuth()
  const [lessons, setLessons] = useState<AssignedLesson[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAssignments = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setError(null)

    const { data: assignments, error: assignErr } = await supabase
      .from('curriculum_lesson_assignments')
      .select('id, lesson_id, assigned_at, in_car_completed_at, video_unlocked_at, curriculum_version_lessons(title, description)')
      .eq('student_id', user.id)
      .order('assigned_at', { ascending: false })

    if (assignErr) { setError('Could not load your lessons.'); setLoading(false); return }

    const lessonIds = (assignments ?? []).map((a: any) => a.lesson_id)
    const { data: assetRows } = lessonIds.length
      ? await supabase
          .from('curriculum_lesson_assets')
          .select('id, lesson_id, asset_type')
          .in('lesson_id', lessonIds)
          .eq('status', 'active')
      : { data: [] as any[] }

    setLessons((assignments ?? []).map((a: any) => {
      const ownAssets = (assetRows ?? []).filter((r: any) => r.lesson_id === a.lesson_id)
      return {
        assignmentId: a.id,
        lessonId: a.lesson_id,
        lessonTitle: a.curriculum_version_lessons?.title ?? 'Lesson',
        lessonDescription: a.curriculum_version_lessons?.description ?? null,
        assignedAt: a.assigned_at,
        inCarCompletedAt: a.in_car_completed_at,
        videoUnlockedAt: a.video_unlocked_at,
        pdfAssetId: ownAssets.find((r: any) => r.asset_type === 'pdf')?.id ?? null,
        videoAssetId: ownAssets.find((r: any) => r.asset_type === 'video')?.id ?? null,
      }
    }))
    setLoading(false)
  }, [user])

  useEffect(() => { fetchAssignments() }, [fetchAssignments])

  return { lessons, loading, error, refresh: fetchAssignments }
}
